import { createPortal } from 'react-dom';
import { Download } from 'lucide-react';
import { useEffect } from 'react';
import { RowWithId } from '@/components/table/types/RowWithId.ts';
import {
  BulkAction,
  BulkActionContext,
} from '@/components/table/types/BulkAction.ts';
import { useTableStoreContext } from '@/components/table/contexts/TableContext.tsx';
import { useSelectedRows } from '@/components/table/hooks/useSelectedRows.ts';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import BulkActionButton from './BulkActionButton';
import SelectionCount from './SelectionCount';
import { useBulkActionRunner } from './useBulkActionRunner';
import { useSelectionExport } from './useSelectionExport';

/**
 * Floating, centered action bar (ClickUp-style) that appears whenever at least
 * one row is selected. Renders the selection count, a clear button, a built-in
 * "export selected" action and any context-specific {@link BulkAction}s.
 */
const TableSelectionBar = <T extends RowWithId>({
  bulkActions,
  exportable = true,
}: {
  bulkActions: BulkAction<T>[];
  /** Show the built-in "export selected" button (default: true). */
  exportable?: boolean;
}) => {
  const selectedRows = useSelectedRows<T>();
  const clearSelection = useTableStoreContext(
    (state) => state.clearRowSelection,
  );

  const count = selectedRows.length;
  const hasSelection = count > 0;

  const ctx: BulkActionContext = {
    selectedIds: selectedRows.map((row) => row.id),
    clearSelection,
  };

  const exportSelection = useSelectionExport(selectedRows);
  const { runningId, runAction } = useBulkActionRunner(selectedRows, ctx);

  // Esc clears the selection while the bar is visible.
  useEffect(() => {
    if (!hasSelection) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearSelection();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hasSelection, clearSelection]);

  const visibleActions = bulkActions.filter(
    (action) => action.visible?.(selectedRows) ?? true,
  );

  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center px-4">
      {hasSelection && (
        <div className="pointer-events-auto flex items-center gap-2 rounded-xl border border-neutral-200 bg-basic-white px-2.5 py-2 shadow-lg duration-200 ease-out animate-in fade-in-0 slide-in-from-bottom-4">
          <SelectionCount count={count} onClear={clearSelection} />

          <div className="h-5 w-px bg-neutral-200" />

          <div className="flex items-center gap-1">
            {exportable && (
              <ActionButton
                tone="outline"
                size="xs"
                onClick={exportSelection}
                disabled={runningId !== null}
              >
                <Download />
                Eksportuj
              </ActionButton>
            )}

            {visibleActions.map((action) => (
              <BulkActionButton
                key={action.id}
                action={action}
                rows={selectedRows}
                ctx={ctx}
                isBarBusy={runningId !== null}
                isRunning={runningId === action.id}
                onRun={runAction}
              />
            ))}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};

export default TableSelectionBar;
